import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { Pencil, Trash2, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { organizationsUserGroupsPartialUpdateMutation } from 'src/api/main'
import type { UserGroup } from 'src/api/main'
import { DateTimeCell, ResizableDrawer, UserCell } from 'src/components'
import { paths } from 'src/router'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserGroups, useApplicationNameMap } from '../hooks'

const { Text, Link, Title } = Typography

/** Rows shown before the list collapses behind a "show all" link. */
const MEMBER_PREVIEW_LIMIT = 8

type Props = {
  workspaceId: number
  group: UserGroup | null
  onClose: () => void
  onEdit: (group: UserGroup) => void
  onDelete: (group: UserGroup) => void
  /** False when the viewer holds `groups.view` but not `groups.manage`. */
  canManage?: boolean
}

/**
 * Read-side view of a group, opened by clicking its row.
 *
 * Mirrors the team detail drawer: identity in the header, membership and
 * granted roles as cards, metadata last. Writes that change more than one
 * field go through the form drawer — only removing a single member is offered
 * inline, because that is the one action the list itself implies.
 */
export const UserGroupDetailDrawer = ({
  workspaceId,
  group,
  onClose,
  onEdit,
  onDelete,
  canManage = true
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()

  const [showAllMembers, setShowAllMembers] = useState(false)
  const { appNameById } = useApplicationNameMap(workspaceId, group != null)

  const { mutateAsync: patchGroup, isPending: removing } = useMutation(
    organizationsUserGroupsPartialUpdateMutation()
  )

  if (!group) return null

  const members = group.users ?? []
  const roles = group.roles ?? []
  const visibleMembers = showAllMembers
    ? members
    : members.slice(0, MEMBER_PREVIEW_LIMIT)

  const goToUser = (userId: number) => {
    navigate(
      paths.workspaces.membersPerson(String(workspaceId), String(userId))
    )
    onClose()
  }

  // Core replaces the whole `users` set, so removing one member means sending
  // everyone else back.
  const confirmRemove = (userId: number, name: string) => {
    modal.confirm({
      title: t('users.groups.detail.removeConfirm.title'),
      content: t('users.groups.detail.removeConfirm.content', {
        name,
        group: group.name
      }),
      okText: t('users.groups.detail.removeConfirm.confirm'),
      okButtonProps: { danger: true },
      cancelText: t('users.groups.form.cancel'),
      onOk: async () => {
        try {
          await patchGroup({
            path: { id: group.id },
            body: {
              users: members.filter((m) => m.id !== userId).map((m) => m.id)
            }
          })
          await invalidateWorkspaceUserGroups(queryClient, workspaceId)
          message.success(t('users.groups.detail.removeSuccess'))
        } catch (error) {
          showApiErrors(error)
        }
      }
    })
  }

  return (
    <ResizableDrawer
      open={group != null}
      onClose={onClose}
      defaultWidth={520}
      storageKey="user-group-detail-drawer-width"
      title={
        <Flex align="center" gap={10}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 7,
              background: 'var(--ant-color-primary-bg)',
              color: 'var(--ant-color-primary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <UsersRound size={15} />
          </span>
          <Flex vertical style={{ lineHeight: 1.2, minWidth: 0 }}>
            <Title level={4} style={{ margin: 0, fontSize: 16 }} ellipsis>
              {group.name}
            </Title>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('users.groups.memberCount', { count: members.length })}
            </Text>
          </Flex>
        </Flex>
      }
      extra={
        canManage ? (
          <Space size="small">
            <Button
              size="small"
              danger
              icon={<Trash2 size={14} />}
              onClick={() => onDelete(group)}
            >
              {t('users.groups.actions.delete')}
            </Button>
            <Button
              size="small"
              type="primary"
              icon={<Pencil size={14} />}
              onClick={() => onEdit(group)}
            >
              {t('users.groups.actions.edit')}
            </Button>
          </Space>
        ) : null
      }
    >
      <Flex vertical gap={14}>
        <Card
          size="small"
          title={
            <Flex align="center" gap={6}>
              <Text strong style={{ fontSize: 13 }}>
                {t('users.groups.form.members')}
              </Text>
              <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
                {members.length}
              </Tag>
            </Flex>
          }
        >
          {members.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '8px 0' }}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('users.groups.form.membersEmpty')}
                </Text>
              }
            />
          ) : (
            <Flex vertical gap={2}>
              {visibleMembers.map((member) => {
                const name =
                  `${member.first_name} ${member.last_name}`.trim() ||
                  member.email

                return (
                  <Flex
                    key={member.id}
                    align="center"
                    justify="space-between"
                    gap={10}
                    style={{
                      padding: '7px 6px',
                      borderBottom:
                        '1px solid var(--ant-color-border-secondary)'
                    }}
                  >
                    <UserCell
                      firstName={member.first_name}
                      lastName={member.last_name}
                      email={member.email}
                      photoUrl={member.photo?.file ?? null}
                      size="md"
                      onClick={() => goToUser(member.id)}
                    />
                    {canManage ? (
                      <Tooltip title={t('users.groups.form.removeMember')}>
                        <Button
                          type="text"
                          size="small"
                          danger
                          loading={removing}
                          icon={<Trash2 size={13} />}
                          onClick={() => confirmRemove(member.id, name)}
                          aria-label={t('users.groups.form.removeMember')}
                        />
                      </Tooltip>
                    ) : null}
                  </Flex>
                )
              })}
              {members.length > MEMBER_PREVIEW_LIMIT && !showAllMembers ? (
                <Link
                  style={{ fontSize: 12, marginTop: 4 }}
                  onClick={() => setShowAllMembers(true)}
                >
                  {t('users.groups.detail.showAllMembers', {
                    count: members.length
                  })}
                </Link>
              ) : null}
            </Flex>
          )}
        </Card>

        <Card
          size="small"
          title={
            <Flex align="center" gap={6}>
              <Text strong style={{ fontSize: 13 }}>
                {t('users.groups.form.roles')}
              </Text>
              <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
                {roles.length}
              </Tag>
            </Flex>
          }
        >
          {roles.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('users.groups.detail.rolesEmpty')}
            </Text>
          ) : (
            <Flex gap={4} wrap="wrap">
              {roles.map((role) => (
                <Tag key={role.id} style={{ marginInlineEnd: 0 }}>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {role.application == null
                      ? t('users.groups.organizationScope')
                      : (appNameById.get(role.application) ??
                        `#${role.application}`)}
                  </Text>
                  {` · ${role.name}`}
                </Tag>
              ))}
            </Flex>
          )}
        </Card>

        <Card size="small" title={t('users.groups.detail.metadata')}>
          <Descriptions column={1} size="small" colon={false}>
            <Descriptions.Item label={t('users.groups.columns.created')}>
              <DateTimeCell value={group.date_created} variant="date" />
            </Descriptions.Item>
            <Descriptions.Item label={t('users.groups.columns.updated')}>
              <DateTimeCell value={group.date_updated} variant="auto" />
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Flex>
    </ResizableDrawer>
  )
}
